import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { auditApi } from '../services/api';
import { AuditLogEntry } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import { TableSkeleton } from '../components/SkeletonLoaders';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import { useApiQuery } from '../hooks/useApiQuery';

const AUDIT_ACTIONS = [
  'vm:start',
  'vm:stop',
  'password:view',
  'run:extend',
  'workload:create',
  'workload:update',
  'workload:delete',
  'installer:delete',
];

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 25;
const DRAWER_WIDTH = 400;

function formatTimestamp(iso: string): string {
  return formatDateSafe(iso, 'dd.MM.yyyy HH:mm:ss');
}

function buildCsvContent(entries: AuditLogEntry[]): string {
  const BOM = '\uFEFF';
  const header = 'Zeitstempel;Benutzer;Aktion;Ressource;Ergebnis;Fehler';
  const rows = entries.map((e) => {
    const timestamp = formatTimestamp(e.timestamp);
    const result = e.result === 'success' ? 'Erfolg' : 'Fehler';
    const error = e.errorMessage ? `"${e.errorMessage.replace(/"/g, '""')}"` : '';
    return `${timestamp};${e.userEmail};${e.action};${e.resourceId || ''};${result};${error}`;
  });
  return BOM + [header, ...rows].join('\n');
}

function triggerCsvDownload(content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `audit-log-${formatDateSafe(new Date(), 'yyyy-MM-dd')}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function AuditPage() {
  const { hasRole } = useAuth();
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [drawerEntry, setDrawerEntry] = useState<AuditLogEntry | null>(null);
  const isAdmin = hasRole('admin');

  const { data: entries, loading, error, refetch } = useApiQuery<AuditLogEntry[]>(
    () => auditApi.list({
      action: actionFilter || undefined,
      from: fromFilter || undefined,
      to: toFilter || undefined,
    }),
    [actionFilter, isAdmin],
    { enabled: isAdmin }
  );

  // Client-side filtering (user email + result)
  const filtered = useMemo(() => {
    return (entries || []).filter((e) => {
      if (userFilter && !e.userEmail.toLowerCase().includes(userFilter.toLowerCase())) {
        return false;
      }
      if (resultFilter && e.result !== resultFilter) {
        return false;
      }
      return true;
    });
  }, [entries, userFilter, resultFilter]);

  // Paginated slice
  const paginatedEntries = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  // Reset to first page when filters change
  const handleUserFilterChange = useCallback((value: string) => {
    setUserFilter(value);
    setPage(0);
  }, []);

  const handleActionFilterChange = useCallback((value: string) => {
    setActionFilter(value);
    setPage(0);
  }, []);

  const handleResultFilterChange = useCallback((value: string) => {
    setResultFilter(value);
    setPage(0);
  }, []);

  const handleFromFilterChange = useCallback((value: string) => {
    setFromFilter(value);
    setPage(0);
  }, []);

  const handleToFilterChange = useCallback((value: string) => {
    setToFilter(value);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleExportCsv = useCallback(() => {
    const content = buildCsvContent(filtered);
    triggerCsvDownload(content);
  }, [filtered]);

  const handleRowClick = useCallback((entry: AuditLogEntry) => {
    setDrawerEntry(entry);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerEntry(null);
  }, []);

  const hasActiveFilters = !!(userFilter || actionFilter || resultFilter || fromFilter || toFilter);

  // Guard: Admin only
  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error">
          Zugriff verweigert — diese Seite ist nur für Admins zugänglich.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Audit-Log</Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="CSV Export">
            <span>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCsv}
                disabled={loading || filtered.length === 0}
              >
                CSV Export
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={refetch} size="large">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <ErrorMessage
          message={error}
          title="Fehler beim Laden des Audit-Logs"
          onRetry={refetch}
        />
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Benutzer (E-Mail)"
                value={userFilter}
                onChange={(e) => handleUserFilterChange(e.target.value)}
                placeholder="filter@nupis.de"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Aktion"
                value={actionFilter}
                onChange={(e) => handleActionFilterChange(e.target.value)}
                data-testid="action-filter"
              >
                <MenuItem value="">Alle</MenuItem>
                {AUDIT_ACTIONS.map((a) => (
                  <MenuItem key={a} value={a}>
                    {a}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Ergebnis"
                value={resultFilter}
                onChange={(e) => handleResultFilterChange(e.target.value)}
                data-testid="result-filter"
              >
                <MenuItem value="">Alle</MenuItem>
                <MenuItem value="success">Erfolg</MenuItem>
                <MenuItem value="error">Fehler</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                label="Von"
                type="date"
                value={fromFilter}
                onChange={(e) => handleFromFilterChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                label="Bis"
                type="date"
                value={toFilter}
                onChange={(e) => handleToFilterChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        {loading ? (
          <TableSkeleton
            rows={8}
            columns={6}
            headers={['Zeitstempel', 'Benutzer', 'Aktion', 'Ressource', 'Ergebnis', 'Fehler']}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            variant={hasActiveFilters ? 'no-results' : 'empty'}
            title="Keine Audit-Einträge gefunden"
            description={
              hasActiveFilters
                ? 'Mit den aktuellen Filtern wurden keine Einträge gefunden.'
                : 'Es wurden noch keine auditpflichtigen Aktionen ausgeführt.'
            }
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Zeitstempel</TableCell>
                  <TableCell>Benutzer</TableCell>
                  <TableCell>Aktion</TableCell>
                  <TableCell>Ressource</TableCell>
                  <TableCell>Ergebnis</TableCell>
                  <TableCell>Fehler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedEntries.map((entry, idx) => (
                  <TableRow
                    key={`${entry.timestamp}-${idx}`}
                    hover
                    onClick={() => handleRowClick(entry)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatTimestamp(entry.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{entry.userEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.action}
                        size="small"
                        variant="outlined"
                        color={entry.action.startsWith('vm:') ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {entry.resourceId || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.result === 'success' ? 'Erfolg' : 'Fehler'}
                        size="small"
                        color={entry.result === 'success' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entry.errorMessage || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              labelRowsPerPage="Einträge pro Seite:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} von ${count}`
              }
            />
          </TableContainer>
        )}
      </Card>

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerEntry !== null}
        onClose={handleDrawerClose}
        PaperProps={{ sx: { width: DRAWER_WIDTH } }}
      >
        {drawerEntry && (
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Audit-Details</Typography>
              <IconButton onClick={handleDrawerClose} aria-label="Schließen">
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box display="flex" flexDirection="column" gap={2.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Zeitstempel
                </Typography>
                <Typography variant="body1">
                  {formatTimestamp(drawerEntry.timestamp)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Benutzer
                </Typography>
                <Typography variant="body1">{drawerEntry.userEmail}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  User ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {drawerEntry.userId}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Aktion
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={drawerEntry.action}
                    size="small"
                    variant="outlined"
                    color={drawerEntry.action.startsWith('vm:') ? 'primary' : 'default'}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Ressource
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {drawerEntry.resourceId || '—'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Ergebnis
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={drawerEntry.result === 'success' ? 'Erfolg' : 'Fehler'}
                    size="small"
                    color={drawerEntry.result === 'success' ? 'success' : 'error'}
                  />
                </Box>
              </Box>

              {drawerEntry.errorMessage && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Fehlermeldung
                  </Typography>
                  <Typography variant="body2" color="error">
                    {drawerEntry.errorMessage}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
